import { ReactNode } from 'react';
import usePresence from 'use-presence';

import Loading from '@cartwhl/ui/atoms/Loading';
import Text from '@cartwhl/ui/base/Text';
import useMediaQuery from '@cartwhl/ui/hooks/useMediaQuery';
import ChevronLeftIcon from '@cartwhl/ui/icons/ChevronLeftIcon';
import CrossIcon from '@cartwhl/ui/icons/CrossIcon';
import Button from '@cartwhl/ui/input/Button';

export const PANEL_WIDTH = 360;

type PanelProps = {
  className?: string;
  subPanelKey?: string;
  title: string;
  closePanel: () => void;
  children: ReactNode;
  loading?: boolean;
  statusText?: string;
  additionalButtons?: ReactNode;
};

export default function PanelPresence({
  isOpen = true,
  enterTransitionDuration = 0,
  exitTransitionDuration = 0,
  ...rest
}: PanelProps & {
  isOpen?: boolean;
  enterTransitionDuration?: number;
  exitTransitionDuration?: number;
}) {
  const { isMounted } = usePresence(isOpen, {
    enterTransitionDuration,
    exitTransitionDuration,
  });

  if (!isMounted) return null;

  return <Panel {...rest} />;
}

function Panel({
  className = '',
  subPanelKey,
  title,
  closePanel,
  children,
  loading,
  statusText,
  additionalButtons,
}: PanelProps) {
  const isLgMedia = useMediaQuery('(min-width: 1024px)');
  const showBackButton = !!subPanelKey && !isLgMedia; // to close the subpanel

  return (
    <div
      className="dark:bg-gray-90 relative h-full bg-white"
      style={{ width: PANEL_WIDTH }}
    >
      <div id="sub-panel" />
      <div
        id="main-panel-scroll-area"
        className={`h-full w-full overflow-x-hidden lg:overflow-y-auto ${className} lg:pl-4`}
      >
        <div
          id="panel-header"
          className="dark:bg-gray-90 sticky top-0 z-10 bg-white lg:-ml-5 lg:pl-5"
        >
          <div className="hidden h-16 items-center justify-end px-4 lg:flex lg:pl-0 lg:pr-6">
            <Button
              variant="tertiary"
              icon={<CrossIcon />}
              onClick={closePanel}
              className="-mr-2.5"
            />
          </div>
          <div className="relative mx-4 flex h-[60px] items-center justify-between pt-1 lg:ml-0 lg:mr-6">
            <div className="flex items-center gap-1">
              {showBackButton && (
                <Button
                  variant="tertiary"
                  className="-ml-3"
                  onClick={closePanel}
                >
                  <ChevronLeftIcon />
                </Button>
              )}
              <Text variant="studio/title/lg" as="h1">
                {title}
              </Text>
            </div>

            <div className="flex items-center gap-1">
              {loading && (
                <Loading className="text-gray-45 dark:text-gray-60" />
              )}
              {!loading && statusText && (
                <Text
                  variant="studio/body/sm"
                  className="text-gray-45 dark:text-gray-60 italic"
                >
                  {statusText}
                </Text>
              )}
              {additionalButtons}
              <Button
                variant="tertiary"
                icon={<CrossIcon />}
                onClick={closePanel}
                className="-mr-2.5 lg:hidden"
              />
            </div>
          </div>
        </div>
        <div className="px-4 pb-6 pt-1 lg:pl-0 lg:pr-6">{children}</div>
      </div>
    </div>
  );
}
